import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function FloatingCTA() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Close collapsed mobile menu on page navigation
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* MOBILE (< md): Collapsed under ONE button, toggles open on click */}
      <div className="md:hidden print:hidden select-none">
        {/* Soft backdrop when opened on mobile to allow tap-outside closing */}
        {isOpen && (
          <div
            className="fixed inset-0 z-40 bg-slate-950/30 backdrop-blur-2xs transition-opacity duration-200"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="fixed right-0 top-1/2 -translate-y-1/2 z-40 flex flex-col items-end">
          {/* Collapsed single button trigger (when closed) */}
          {!isOpen && (
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              aria-label="Åbn hurtigmenu med kontakt og genveje"
              aria-expanded="false"
              className="flex h-15 w-12 flex-col items-center justify-center gap-1 bg-[#dc6002] hover:bg-[#c25301] active:bg-[#a84801] text-white shadow-[-3px_4px_12px_rgba(0,0,0,0.35)] rounded-l-lg cursor-pointer transition-transform active:scale-95"
            >
              <i className="fa-solid fa-phone-volume text-base animate-pulse" aria-hidden="true" />
              <span className="text-[10px] font-bold uppercase tracking-tight leading-none">Menu</span>
            </button>
          )}

          {/* Expanded all 4 buttons (when open) */}
          {isOpen && (
            <div className="flex flex-col gap-2.5 p-0 animate-in slide-in-from-right-8 duration-200">
              {/* Close button at the top */}
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Luk hurtigmenu"
                className="flex h-8 w-26 items-center justify-center gap-1.5 bg-[#0f2b48] hover:bg-[#1a385c] active:bg-[#0a1e33] text-white text-xs font-semibold shadow-md rounded-l-md cursor-pointer transition-colors"
              >
                <i className="fa-solid fa-xmark text-sm" aria-hidden="true" />
                <span>Luk</span>
              </button>

              {/* Action Tile: Phone */}
              <a
                href="tel:76121230"
                aria-label="Ring til Fastlane på 76 12 12 30"
                className="flex h-18 w-26 flex-col items-center justify-center gap-1 bg-[#dc6002] hover:bg-[#c25301] active:bg-[#a84801] text-white shadow-[0_4px_6px_rgba(0,0,0,0.35)] transition-colors rounded-none cursor-pointer group"
              >
                <svg
                  className="size-5.5 fill-current transition-transform duration-200 group-hover:scale-105"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.58.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.58 1 1 0 01-.25 1.01l-2.2 2.2z" />
                </svg>
                <span className="text-xs font-semibold tracking-tight text-center leading-none">
                  76 12 12 30
                </span>
              </a>

              {/* Action Tile: Email */}
              <a
                href="mailto:booking@fastlane.dk"
                aria-label="Send e-mail til booking@fastlane.dk"
                className="flex h-18 w-26 flex-col items-center justify-center gap-1 bg-[#dc6002] hover:bg-[#c25301] active:bg-[#a84801] text-white shadow-[0_4px_6px_rgba(0,0,0,0.35)] transition-colors rounded-none cursor-pointer group"
              >
                <svg
                  className="size-5.5 fill-current transition-transform duration-200 group-hover:scale-105"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
                <span className="text-xs font-semibold tracking-tight text-center leading-none">
                  Send e-mail
                </span>
              </a>

              {/* Action Tile: News */}
              <Link
                to="/nyheder"
                aria-label="Gå til nyheder"
                onClick={() => setIsOpen(false)}
                className="flex h-18 w-26 flex-col items-center justify-center gap-1 bg-[#dc6002] hover:bg-[#c25301] active:bg-[#a84801] text-white shadow-[0_4px_6px_rgba(0,0,0,0.35)] transition-colors rounded-none cursor-pointer group"
              >
                <svg
                  className="size-5.5 fill-none stroke-current stroke-2 transition-transform duration-200 group-hover:scale-105"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
                <span className="text-xs font-semibold tracking-tight text-center leading-none">
                  Nyheder
                </span>
              </Link>

              {/* Action Tile: Shipping / Price */}
              <Link
                to="/fragtrater"
                aria-label="Beregn fragtpris og rater"
                onClick={() => setIsOpen(false)}
                className="flex h-18 w-26 flex-col items-center justify-center gap-1 bg-[#dc6002] hover:bg-[#c25301] active:bg-[#a84801] text-white shadow-[0_4px_6px_rgba(0,0,0,0.35)] transition-colors rounded-none cursor-pointer group"
              >
                <svg
                  className="size-5.5 fill-current transition-transform duration-200 group-hover:scale-105"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M20 8h-3V4H1v13h2a3 3 0 006 0h6a3 3 0 006 0h2v-5l-3-4zM6 18.5A1.5 1.5 0 117.5 17 1.5 1.5 0 016 18.5zm11 0a1.5 1.5 0 111.5-1.5 1.5 1.5 0 01-1.5 1.5z" />
                </svg>
                <span className="text-xs font-semibold tracking-tight text-center leading-none">
                  Fragtpris
                </span>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* DESKTOP (>= md): Always visible all 4 buttons */}
      <aside
        aria-label="Hurtig navigation og kontakt"
        className="hidden md:flex fixed right-0 top-1/2 -translate-y-1/2 z-40 flex-col gap-3 p-0 print:hidden select-none"
      >
        {/* Action Tile: Phone */}
        <a
          href="tel:76121230"
          aria-label="Ring til Fastlane på 76 12 12 30"
          className="flex h-19 w-26 sm:h-20 sm:w-28 flex-col items-center justify-center gap-1.5 bg-[#dc6002] hover:bg-[#c25301] text-white shadow-[0_4px_6px_rgba(0,0,0,0.35)] transition-colors rounded-none cursor-pointer group"
        >
          <svg
            className="size-6 fill-current transition-transform duration-200 group-hover:scale-105"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.58.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.58 1 1 0 01-.25 1.01l-2.2 2.2z" />
          </svg>
          <span className="text-xs sm:text-[13px] font-semibold tracking-tight text-center leading-none">
            76 12 12 30
          </span>
        </a>

        {/* Action Tile: Email */}
        <a
          href="mailto:booking@fastlane.dk"
          aria-label="Send e-mail til booking@fastlane.dk"
          className="flex h-19 w-26 sm:h-20 sm:w-28 flex-col items-center justify-center gap-1.5 bg-[#dc6002] hover:bg-[#c25301] text-white shadow-[0_4px_6px_rgba(0,0,0,0.35)] transition-colors rounded-none cursor-pointer group"
        >
          <svg
            className="size-6 fill-current transition-transform duration-200 group-hover:scale-105"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
          </svg>
          <span className="text-xs sm:text-[13px] font-semibold tracking-tight text-center leading-none">
            Send e-mail
          </span>
        </a>

        {/* Action Tile: News */}
        <Link
          to="/nyheder"
          aria-label="Gå til nyheder"
          className="flex h-19 w-26 sm:h-20 sm:w-28 flex-col items-center justify-center gap-1.5 bg-[#dc6002] hover:bg-[#c25301] text-white shadow-[0_4px_6px_rgba(0,0,0,0.35)] transition-colors rounded-none cursor-pointer group"
        >
          <svg
            className="size-6 fill-none stroke-current stroke-2 transition-transform duration-200 group-hover:scale-105"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
          <span className="text-xs sm:text-[13px] font-semibold tracking-tight text-center leading-none">
            Nyheder
          </span>
        </Link>

        {/* Action Tile: Shipping / Price */}
        <Link
          to="/fragtrater"
          aria-label="Beregn fragtpris og rater"
          className="flex h-19 w-26 sm:h-20 sm:w-28 flex-col items-center justify-center gap-1.5 bg-[#dc6002] hover:bg-[#c25301] text-white shadow-[0_4px_6px_rgba(0,0,0,0.35)] transition-colors rounded-none cursor-pointer group"
        >
          <svg
            className="size-6 fill-current transition-transform duration-200 group-hover:scale-105"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M20 8h-3V4H1v13h2a3 3 0 006 0h6a3 3 0 006 0h2v-5l-3-4zM6 18.5A1.5 1.5 0 117.5 17 1.5 1.5 0 016 18.5zm11 0a1.5 1.5 0 111.5-1.5 1.5 1.5 0 01-1.5 1.5z" />
          </svg>
          <span className="text-xs sm:text-[13px] font-semibold tracking-tight text-center leading-none">
            Fragtpris
          </span>
        </Link>
      </aside>
    </>
  );
}
